/**
 * ImportModal.tsx
 *
 * Import lines from an existing published BOM into Master lines.
 * Spec: docs/superpowers/plans/2026-09-22-bom-master-composer-spec.md
 */
import { useEffect, useState } from 'react'
import { X, Download, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { importMasterBomApi } from '@/services/boms/masterApiClient'
import type { MasterComboRow } from '@/services/boms/masterTypes'

interface ImportModalProps {
  isOpen: boolean
  onClose: () => void
  combos: MasterComboRow[]
  hasExistingMasterLines: boolean
  productId: string
  onImportSuccess: () => void
}

export function ImportModal({
  isOpen,
  onClose,
  combos,
  hasExistingMasterLines,
  productId,
  onImportSuccess,
}: ImportModalProps) {
  const [selectedKey, setSelectedKey] = useState<string>('')
  const [confirmed, setConfirmed] = useState<boolean>(false)
  const [importing, setImporting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [successResult, setSuccessResult] = useState<any | null>(null)

  // Filter combos that have a published BOM
  const publishedCombos = combos.filter((c) => Boolean(c.published_bom))

  useEffect(() => {
    if (isOpen) {
      setError(null)
      setSuccessResult(null)
      setConfirmed(false)
      if (publishedCombos.length > 0) {
        setSelectedKey(
          `${publishedCombos[0]?.variant_id}_${publishedCombos[0]?.assembly_type}`,
        )
      } else {
        setSelectedKey('')
      }
    }
  }, [isOpen, combos])

  if (!isOpen) return null

  const targetCombo = publishedCombos.find(
    (c) => `${c.variant_id}_${c.assembly_type}` === selectedKey,
  )

  const handleImport = async () => {
    if (!targetCombo) return
    setImporting(true)
    setError(null)

    try {
      const res = await importMasterBomApi(productId, {
        variant_id: targetCombo.variant_id,
        assembly_type: targetCombo.assembly_type,
      })
      setSuccessResult(res)
      onImportSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nhập từ BOM thất bại')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Download className="h-4 w-4 text-indigo-600" />
              <span>Nhập từ BOM đã publish (Bootstrap)</span>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Sao chép các dòng của một BOM active vào bảng Master BOM
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {successResult ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 space-y-2">
              <div className="flex items-center gap-2 font-bold text-base text-emerald-800">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <span>Nhập dữ liệu thành công!</span>
              </div>
              <p className="text-xs text-emerald-700">
                Đã sao chép <strong>{successResult.imported_wood_count}</strong>{' '}
                chi tiết gỗ và{' '}
                <strong>{successResult.imported_hardware_count}</strong> ngũ kim
                từ {successResult.source_version_label} vào Master BOM.
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg">
                  {error}
                </div>
              )}

              {publishedCombos.length === 0 ? (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                  Sản phẩm này chưa có bất kỳ BOM nào được publish. Không có dữ
                  liệu nguồn để nhập.
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Chọn cấu hình BOM nguồn đã publish
                    </label>
                    <select
                      value={selectedKey}
                      onChange={(e) => setSelectedKey(e.target.value)}
                      disabled={importing}
                      className="w-full text-xs rounded-lg border border-slate-300 py-2 px-3 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {publishedCombos.map((c) => {
                        const key = `${c.variant_id}_${c.assembly_type}`
                        return (
                          <option key={key} value={key}>
                            {c.combo_code} · {c.color_name} ({c.assembly_label})
                            — {c.published_bom?.version_label}
                          </option>
                        )
                      })}
                    </select>
                  </div>

                  {/* Confirmation banner if master already has lines */}
                  {hasExistingMasterLines && (
                    <div className="p-3.5 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-900 space-y-2">
                      <div className="flex items-start gap-2 font-semibold text-amber-800">
                        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                        <span>Master BOM của sản phẩm đã có sẵn dữ liệu</span>
                      </div>
                      <p className="text-slate-600 text-xs leading-relaxed">
                        Hệ thống sẽ <strong>thêm tiếp</strong> các dòng từ BOM
                        nguồn vào bảng Master (áp dụng cho đúng màu và kết cấu
                        này). Dòng cũ sẽ được giữ nguyên không bị ghi đè.
                      </p>
                      <label className="flex items-center gap-2 pt-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmed}
                          onChange={(e) => setConfirmed(e.target.checked)}
                          className="rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="font-semibold text-amber-900">
                          Tôi xác nhận muốn thêm các dòng này vào Master
                        </span>
                      </label>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="secondary" onClick={onClose} disabled={importing}>
            {successResult ? 'Đóng' : 'Hủy'}
          </Button>
          {!successResult && publishedCombos.length > 0 && (
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={
                importing || (hasExistingMasterLines && !confirmed) || !targetCombo
              }
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 px-4"
            >
              <Download className="h-3.5 w-3.5" />
              <span>{importing ? 'Đang nhập...' : 'Xác nhận nhập'}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
